import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  builtAt as localBuiltAt,
  buildId as localBuildId
} from "../../../generated/buildInfo";
import type { AppUpdateDetectorState } from "../types";
import { fetchRemoteVersionInfo, updateKindOf } from "../utils";

export type UseAppUpdateDetectorOptions = {
  pollIntervalMs?: number;
};

export function useAppUpdateDetector(
  options: UseAppUpdateDetectorOptions = {}
): AppUpdateDetectorState {
  const pollIntervalMs = options.pollIntervalMs ?? 60_000;

  const [state, setState] = useState<AppUpdateDetectorState>(() => ({
    update: "none",
    localBuildId
  }));

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const enabled = useMemo(() => typeof window !== "undefined", []);

  const checkOnce = useCallback(async () => {
    if (!enabled) return;
    if (stateRef.current.update === "forced") return;

    try {
      const remote = await fetchRemoteVersionInfo();
      setState((prev) => {
        const next: AppUpdateDetectorState = {
          ...prev,
          lastCheckedAt: Date.now(),
          error: undefined,
          remoteBuildId: remote.buildId
        };

        const update = updateKindOf(
          { buildId: prev.localBuildId, builtAt: localBuiltAt },
          remote
        );
        // A soft update never goes back to none; a later deploy can force it.
        return {
          ...next,
          update: prev.update === "soft" && update === "none" ? "soft" : update
        };
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to check app version";
      setState((prev) => ({
        ...prev,
        lastCheckedAt: Date.now(),
        error: message
      }));
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    // Initial check
    checkOnce();

    // Interval polling
    const id = window.setInterval(() => {
      checkOnce();
    }, pollIntervalMs);

    const onVisibilityOrFocus = () => {
      // Only re-check when coming back to an active tab/window.
      if (document.visibilityState === "visible") {
        checkOnce();
      }
    };

    window.addEventListener("focus", onVisibilityOrFocus);
    document.addEventListener("visibilitychange", onVisibilityOrFocus);

    const onOnline = () => {
      checkOnce();
    };
    window.addEventListener("online", onOnline);

    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onVisibilityOrFocus);
      document.removeEventListener("visibilitychange", onVisibilityOrFocus);
      window.removeEventListener("online", onOnline);
    };
  }, [checkOnce, enabled, pollIntervalMs]);

  return state;
}
